import React, { FC, useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form as FormikForm, Formik } from 'formik';
import { cloneDeep, compact } from 'lodash';
import { Grid } from '@material-ui/core';

import {
  ContentInput,
  Network,
  PostType,
  ProcessContentV2Mutation,
  useProcessContentV2Mutation,
} from 'api';
import {
  useNotify,
  useProtectedClient,
  useCapabilities,
  useIntegrations,
  useReleases,
} from 'hooks';
import { BlockListProvider } from 'providers';
import { EMPTY_SLATE_EDITOR } from 'app-constants';
import * as paths from 'paths';
import cookieStorage, { SHARE_URL_COOKIE } from 'cookie-storage';

import { getSafeBody, formatDisclosures } from '../helpers';
import { PostUIContext, initialPostUIState, postContextReducer } from '../PostContext';

import Post from './Post';
import { Sharing } from '../components/Widgets';
import { useCreatePost } from '../updaters';
import { PostSchema } from '../validations';

const PostCreate: FC = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const client = useProtectedClient();
  const location = useLocation();
  const releases = useReleases();
  const createPostMutationOptions = useCreatePost();
  const { isAdmin, needs } = useCapabilities();
  const integrations = useIntegrations();

  const hasLinkedInMentions = releases.includes('linkedinCompanyMentions');
  const hasBlocklist = releases.includes('blocklist');
  const hasSlack = integrations.some(integration => integration.provider === Network.Slack);
  const hasMSTeams = integrations.some(integration => integration.provider === Network.Msteams);

  const [state, dispatch] = useReducer(postContextReducer, initialPostUIState);

  const { mutate: processContentV2 } = useProcessContentV2Mutation({
    ...createPostMutationOptions,
    onError: notify.mutationError,
    onSuccess: handleCompleted,
  });

  const groupIds = getGroupIds();

  function getGroupIds() {
    if (location?.state?.group) {
      const { group } = location.state;
      return [group.id];
    }

    return [];
  }

  useEffect(() => {
    if (
      groupIds.length &&
      state.moderatorMode &&
      !needs('Access.Moderator', groupIds[0]) &&
      !isAdmin
    ) {
      dispatch({ type: 'moderatorMode', value: false });
      dispatch({ type: 'repost', value: false });
    }
  }, []);

  function getValues(): ContentInput {
    const shareUrl = cookieStorage.shareUrl || '';
    cookieStorage.remove(SHARE_URL_COOKIE);

    return {
      group_ids: groupIds,
      content_source_id: 'manual',
      is_engageable: false,
      is_important: false,
      is_shareable: client.allow_sharing,
      is_pinned: false,
      is_branded: false,
      pinned_end_at: null,
      lock_commentary: false,
      is_important_message: '',
      url_param_values: [],
      pinned_to_groups: [],
      primary_group_id: groupIds[0] || null,
      ms_teams_channels: [],
      slack_channels: [],
      content_tags: [],
      commentary: [
        {
          network: Network.Twitter,
          commentary: '',
          ...(hasBlocklist ? { blockList: [] } : {}),
        },
        {
          network: Network.Linkedin,
          commentary: '',
          ...(hasLinkedInMentions ? { mentions: [] } : {}),
          ...(hasBlocklist ? { blockList: [] } : {}),
        },
        {
          network: Network.Facebook,
          commentary: '',
          ...(hasBlocklist ? { blockList: [] } : {}),
        },
        {
          network: Network.Instagram,
          commentary: '',
          ...(hasBlocklist ? { blockList: [] } : {}),
        },
      ],
      disclosures: [
        { network: Network.Twitter, disclosure_id: '' },
        { network: Network.Linkedin, disclosure_id: '' },
        { network: Network.Facebook, disclosure_id: '' },
        { network: Network.Instagram, disclosure_id: '' },
      ],
      schedule: {
        publish: null,
        expire: null,
      },
      content_object: {
        post_type: PostType.Manual,
        body: EMPTY_SLATE_EDITOR,
        url: shareUrl,
        title: '',
        photos: [],
        media: null,
        url_metadata_override: {
          title: '',
          description: '',
        },
      },
    };
  }

  function handleCompleted({ processContentV2 }: ProcessContentV2Mutation) {
    if (!state.share) {
      notify.info({ i18n: { i18nKey: 'posting:created' } });
      handleNavigation();
      return;
    }

    navigate(`/content/${processContentV2.content.content_id}`);
  }

  function handleNavigation() {
    window.setTimeout(() => {
      const isModal = paths.postCreate !== location.pathname;
      isModal ? navigate(-1) : navigate(paths.index);
    }, 100);
  }

  function onSubmit(contentInput: ContentInput) {
    const contentToSubmit: ContentInput = cloneDeep(contentInput);

    contentToSubmit.content_object.photos = contentInput.content_object.photos.filter((_, idx) =>
      state.selectedPhotoIndexes.includes(idx)
    );

    contentToSubmit.group_ids = compact(contentToSubmit.group_ids);
    contentToSubmit.content_object.body = getSafeBody(contentToSubmit);
    contentToSubmit.commentary = contentToSubmit.commentary.filter(c => !!c.commentary);
    contentToSubmit.disclosures = formatDisclosures(contentToSubmit);

    if (!state.contentTagging) {
      contentToSubmit.content_tags = [];
    }

    if (!state.slack) {
      contentToSubmit.slack_channels = [];

      if (!hasSlack) {
        delete contentToSubmit.slack_channels;
      }
    }

    if (!state.msteams) {
      contentToSubmit.ms_teams_channels = [];

      if (!hasMSTeams) {
        delete contentToSubmit.ms_teams_channels;
      }
    }

    delete contentToSubmit.content_object.url_metadata;

    if (hasBlocklist) {
      contentToSubmit.commentary = contentToSubmit.commentary.map(share => ({
        commentary: share.commentary,
        network: share.network,
        ...(hasLinkedInMentions ? { mentions: share.mentions || [] } : {}),
      }));
    }

    processContentV2({ content: contentToSubmit });
  }

  return (
    <PostUIContext.Provider value={{ state, dispatch }}>
      <BlockListProvider client_id={client.id}>
        <Formik
          validateOnChange={true}
          onSubmit={onSubmit}
          initialValues={getValues()}
          validationSchema={PostSchema}
          options={{ uiState: state }}
        >
          <FormikForm>
            <Post>
              <Grid item container>
                <Sharing />
              </Grid>
            </Post>
          </FormikForm>
        </Formik>
      </BlockListProvider>
    </PostUIContext.Provider>
  );
};

export default PostCreate;
